import { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import moment from 'moment';

import useQueryParam from './useQueryParam';

const periodToDatePickerMode = {
  daily: 'date',
  weekly: 'week',
  monthly: 'month',
  yearly: 'year',
};

const useIsMounted = () => {
  const isMounted = useRef(false)

  useEffect(() => {
    isMounted.current = true

    return () => {
      isMounted.current = false
    };
  }, [])

  return isMounted.current;
};

const useDatePicker = (period = 'daily', opts = {}) => {
  const { queryParam = '', initialValue = null } = opts;

  const [queryValue, setQueryValue] = useQueryParam(queryParam, initialValue);
  const [date, setDate] = useState(() => {
    let dt = initialValue;
    if (queryParam) {
      if (typeof queryValue === 'string' && queryValue) {
        dt = moment(queryValue, 'YYYY-MM-DD').startOf('day');
      }
    }
    return dt || moment();
  });
  const mode = useMemo(() => {
    let mode = periodToDatePickerMode[period];
    if (!mode) {
      console.warn(`useDatePicker: wrong period value ${period}`);
      mode = periodToDatePickerMode.daily;
    }
    return mode;
  }, [period]);

  // eslint-disable-next-line no-unused-vars
  const isMounted = useIsMounted();
  useEffect(() => {
    if (mode) {
      const dt = moment().startOf(mode === 'date' ? 'day' : mode);
      setDate(dt);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mode]);

  useEffect(() => {
    if (queryParam) {
      if (date) {
        setQueryValue(date.format('YYYY-MM-DD'));
      } else {
        setQueryValue('');
      }
    }
  }, [queryParam, setQueryValue, date]);

  const onDateChanged = useCallback((date) => {
    if (date === null) {
      setDate(null);
    } else {
      setDate(date.startOf(mode === 'date' ? 'day' : mode))
    }
  }, [mode]);

  return {
    mode,
    date,
    setDate: onDateChanged,
  };
};

export const useDateRangePicker = (period = 'daily', opts = {}) => {
  const { queryParam = '', initialValue = [null, null] } = opts;

  const [startQueryValue, setStartQueryValue] = useQueryParam(
    queryParam !== '' ? `${queryParam}Start` : '',
    initialValue[0],
  );
  const [endQueryValue, setEndQueryValue] = useQueryParam(
    queryParam !== '' ? `${queryParam}End` : '',
    initialValue[1],
  );

  const [dateRange, setDateRange] = useState(() => {
    const range = initialValue;
    if (queryParam) {
      if (typeof startQueryValue === 'string' && startQueryValue) {
        range[0] = moment(startQueryValue, 'YYYY-MM-DD').startOf('day');
      }
      if (typeof endQueryValue === 'string' && endQueryValue) {
        range[1] = moment(endQueryValue, 'YYYY-MM-DD').endOf('day');
      }
    }
    return range;
  });

  const mode = useMemo(() => {
    let mode = periodToDatePickerMode[period];
    if (!mode) {
      console.warn(`useDatePicker: wrong period value ${period}`);
      mode = periodToDatePickerMode.daily;
    }
    return mode;
  }, [period]);

  // eslint-disable-next-line no-unused-vars
  const prevModeRef = useRef(null);
  const isFirstRenderRef = useRef(true);
  useEffect(() => {
    if (!mode) return;

    setDateRange(prev => {
      const unit = mode === 'date' ? 'day' : mode;
      const hasExisting = Array.isArray(prev) && (prev[0] || prev[1]);
      const prevMode = prevModeRef.current;

      prevModeRef.current = mode;

      if (isFirstRenderRef.current) {
        isFirstRenderRef.current = false;
        if (hasExisting) return prev;
        const start = moment().startOf(unit);
        const end = moment().endOf(unit);
        return [start, end];
      }

      if (prevMode && prevMode !== mode) {
        const start = moment().startOf(unit);
        const end = moment().endOf(unit);
        return [start, end];
      }

      // keep current range
      return prev;
    });
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mode]);

  const onDateRangeChanged = useCallback(([start, end]) => {
    setDateRange([
      start !== null ? start.startOf(mode === 'date' ? 'day' : mode) : null,
      end !== null ? end.endOf(mode === 'date' ? 'day' : mode) : null
    ]);
  }, [mode]);

  useEffect(() => {
    if (queryParam) {
      if (dateRange[0]) {
        setStartQueryValue(dateRange[0].format('YYYY-MM-DD'));
      } else {
        setStartQueryValue('');
      }
      if (dateRange[1]) {
        setEndQueryValue(dateRange[1].format('YYYY-MM-DD'));
      } else {
        setEndQueryValue('');
      }
    }
  }, [queryParam, setStartQueryValue, setEndQueryValue, dateRange]);

  return {
    mode,
    dateRange,
    setDateRange: onDateRangeChanged,
  };
};

export default useDatePicker;
